<template>
  <v-row>
    <!-- LEFT: FORM -->
    <v-col cols="12" md="6">
      <v-card
        flat
        class="qt-form-card d-flex flex-column"
        height="calc(100vh - 82px)"
      >
        <div class="qt-form-header pl-base-bg">
          <div class="qt-form-header-left">
            <div class="qt-form-header-icon">
              <v-icon color="#fff" size="22">mdi-file-document-edit-outline</v-icon>
            </div>
            <div>
              <div class="qt-form-title">Create Quotation</div>
              <div class="qt-form-subtitle">Fill in the details to generate a new quotation</div>
            </div>
          </div>
        </div>

        <v-form ref="form" v-model="valid" class="pa-6 overflow-y-auto flex-grow-1">
          <v-row dense>
            <v-col :cols="isMobile ? 12 : 4">
              <v-text-field
                v-model="form.quotationNumber"
                label="Quotation Number"
                outlined
                dense
                prepend-inner-icon="mdi-pound"
                :rules="[rules.required, rules.uniqueQuotation]"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="quotationMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.quotationDate)"
                    label="Quotation Date"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    :rules="[rules.required]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.quotationDate" @change="quotationMenu = false" />
              </v-menu>
            </v-col>

            <!-- VALID UNTIL — Quotation's analogue of Invoice's Due Date -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="validUntilMenu"
                :close-on-content-click="false"
                offset-y
                left
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.validUntil)"
                    label="Valid Until"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar-clock"
                    append-icon="mdi-close-circle"
                    @click:append="form.validUntil = null"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.validUntil" @change="validUntilMenu = false" />
              </v-menu>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-autocomplete
                v-model="selectedCustomer"
                :items="customers"
                item-text="name"
                item-value="_id"
                label="Select Customer"
                outlined
                dense
                prepend-inner-icon="mdi-account"
                return-object
                @change="onCustomerSelect"
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="primary"
                        style="cursor: pointer"
                        @click="customerDialog = true"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>New Customer</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.address"
                label="Customer Address"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.gstin"
                label="Customer GSTIN"
                outlined
                dense
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.placeOfSupply"
                label="Place of Supply (State)"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker-radius"
              />
            </v-col>

            <v-col cols="12">
              <div class="qt-section-label">Items</div>

              <div
                v-for="(item, index) in form.items"
                :key="index"
                class="qt-item-row"
              >
                <v-btn icon class="qt-item-remove" @click="removeItem(index)">
                  <v-icon color="error" size="24">mdi-delete-outline</v-icon>
                </v-btn>

                <v-row dense>
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-autocomplete
                      v-model="item.productObj"
                      :items="products"
                      item-text="productName"
                      return-object
                      label="Product"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-cube-outline"
                      @change="onProductSelect(item, index)"
                    >
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              color="primary"
                              style="cursor: pointer"
                              @click="openProductDialog(index)"
                            >
                              mdi-plus
                            </v-icon>
                          </template>
                          <span>New Product</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model="item.hsn"
                      label="HSN/SAC"
                      dense
                      outlined
                      hide-details
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      label="Qty"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>
                </v-row>

                <v-row dense class="mt-1">
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.price"
                      type="number"
                      label="Rate"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.taxPercent"
                      type="number"
                      label="Tax % (estimate)"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      :value="formatAmount(item.amount)"
                      label="Amount"
                      dense
                      outlined
                      readonly
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <v-btn small class="qt-add-item-btn" @click="addItem">
                <v-icon left size="16">mdi-plus</v-icon>
                Add Product
              </v-btn>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-4"></v-divider>

              <div class="totals-grid">
                <div>Subtotal</div>
                <div>₹ {{ formatAmount(subtotal) }}</div>

                <div>Estimated Tax</div>
                <div>₹ {{ formatAmount(gst) }}</div>
              </div>

              <div class="total-row mt-2">
                <span>Total</span>
                <strong>₹ {{ formatAmount(totalAmount) }}</strong>
              </div>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="1"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-row class="qt-form-actions px-6 flex-grow-0 flex-shrink-0" justify="end" dense>
          <v-col cols="6" sm="auto">
            <v-btn block @click="reset" color="secondary">Reset</v-btn>
          </v-col>

          <v-col v-if="!isViewOnly" cols="6" sm="auto">
            <v-btn
              block
              class="qt-save-btn"
              :loading="loading"
              @click="submit"
              :disabled="
                totalAmount <= 0 ||
                !form.customer.name ||
                !form.quotationNumber ||
                !pdfGenerated
              "
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- RIGHT PREVIEW -->
    <v-col cols="12" md="6">
      <v-card class="qt-preview-card d-flex flex-column" flat height="calc(100vh - 82px)">
        <div class="qt-preview-header">
          <v-icon color="#0a5a90" class="mr-2">mdi-file-eye-outline</v-icon>
          <h3 class="qt-preview-title">Document Preview</h3>
        </div>

        <v-row class="px-4 flex-grow-0 flex-shrink-0" no-gutters>
          <v-col cols="12">
            <v-btn
              block
              class="qt-generate-btn"
              @click="autoGenerateQuotation"
              :disabled="totalAmount <= 0 || !form.customer.name || !form.quotationNumber"
            >
              Generate Quotation
            </v-btn>
            <div v-if="!pdfGenerated" class="mt-2 text-caption" style="color: #b71c1c">
              Generate the quotation PDF before saving.
            </div>
          </v-col>
        </v-row>

        <div class="viewer mt-4 mx-4">
          <img v-if="preview && isImage" :src="preview" class="preview-img" />
          <iframe v-else-if="preview" :src="preview" class="preview-frame"></iframe>
          <div v-else class="empty-state">
            <v-icon size="40" color="#cbd5e1">mdi-file-document-outline</v-icon>
            <div class="mt-2">No document selected</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <GlobalDialog v-model="customerDialog" title="New Customer" :width="700">
      <CreateCustomer mode="dialog" @created="onCustomerCreated" />
    </GlobalDialog>

    <GlobalDialog v-model="productDialog" title="New Product" :width="600">
      <CreateProduct mode="dialog" @created="onProductCreated" />
    </GlobalDialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";
import CreateCustomer from "./create-customer.vue";
import CreateProduct from "./create-product.vue";
import { resolvePlaceOfSupplyState } from "../utils/gst";
import { PDF_COLORS, makePdfPrimitives } from "../utils/pdf-helpers";

export default {
  name: "CreateQuotation",
  components: {
    GlobalDialog,
    CreateCustomer,
    CreateProduct,
  },

  async created() {
    this.fetchQuotations();
    this.fetchQuotationNumber();
    this.fetchProducts();

    await this.fetchCustomers();
  },

  data() {
    const getISTDate = () => {
      return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    };
    return {
      valid: false,
      loading: false,
      file: null,
      preview: null,
      pdfGenerated: false,
      activeItemIndex: null,

      quotationMenu: false,
      validUntilMenu: false,
      selectedCustomer: null,
      customerDialog: false,
      productDialog: false,

      form: {
        quotationNumber: "",
        quotationDate: getISTDate(),
        validUntil: null,
        placeOfSupply: "",

        customer: {
          customerId: null,
          name: "",
          address: "",
          gstin: "",
          state: "",
        },

        items: [
          {
            productId: null,
            product: "",
            quantity: 1,
            hsn: "",
            price: 0,
            taxPercent: 18,
            amount: 0,
          },
        ],

        note: "",
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueQuotation: (v) => {
          if (!v || !this.quotations.length) return true;
          return (
            !this.quotations.some(
              (q) => q.quotationNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Quotation number already exists"
          );
        },
      },
    };
  },

  watch: {
    "form.items": {
      deep: true,
      handler() {
        this.form.items.forEach((_, i) => this.calcItem(i));
      },
    },
    form: {
      deep: true,
      handler() {
        this.pdfGenerated = false;
      },
    },
  },

  computed: {
    quotations() {
      return this.$store.state.quotations.list || [];
    },

    subtotal() {
      return this.form.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    },

    gst() {
      return this.form.items.reduce((sum, item) => {
        return sum + ((item.amount || 0) * (item.taxPercent || 0)) / 100;
      }, 0);
    },

    totalAmount() {
      return this.subtotal + this.gst;
    },

    isImage() {
      return this.file && this.file.type.startsWith("image/");
    },

    profile() {
      return this.$store.state.business?.profile;
    },

    customers() {
      return this.$store.state.customers.customers;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    products() {
      return this.$store.state.products.products;
    },
  },

  methods: {
    ...mapActions("business", ["getProfile"]),

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}/${d.getFullYear()}`;
    },

    formatAmount(val) {
      return Number(val || 0).toFixed(2);
    },

    onCustomerSelect(customer) {
      if (!customer) return;

      const gstin = customer.gstin || "";

      Object.assign(this.form.customer, {
        customerId: customer._id || null,
        name: customer.name || "",
        gstin: customer.gstin || "",
        state: customer.state || "",
        address: [customer.address, customer.city, customer.state, customer.pincode, customer.country]
          .filter(Boolean)
          .join(", "),
      });

      this.form.placeOfSupply =
        resolvePlaceOfSupplyState({ customerGstin: gstin, customerState: customer.state }) || "";
    },

    addItem() {
      this.form.items.push({
        productId: null,
        product: "",
        quantity: 1,
        hsn: "",
        price: 0,
        taxPercent: 18,
        amount: 0,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;

      item.amount = +(qty * price).toFixed(2);
    },

    onProductSelect(item, index) {
      const product = item.productObj;
      if (!product) return;

      item.productId = product._id;
      item.product = product.productName;
      item.hsn = product.hsn;
      item.price = product.rate;

      this.calcItem(index);
    },

    openProductDialog(index) {
      this.activeItemIndex = index;
      this.productDialog = true;
    },

    onCustomerCreated(customer) {
      this.customerDialog = false;
      if (customer) {
        this.selectedCustomer = customer;
        this.onCustomerSelect(customer);
      }
    },

    async onProductCreated(product) {
      this.productDialog = false;
      if (!product) return;

      const index = this.activeItemIndex;
      if (index === null || !this.form.items[index]) return;

      await this.fetchProducts();

      const item = this.form.items[index];
      const selected = this.products.find((p) => p._id === product._id);
      const finalProduct = selected || product;

      this.$set(item, "productObj", finalProduct);
      this.onProductSelect(item, index);

      this.activeItemIndex = null;
    },

    toBase64(file) {
      return new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
      });
    },

    async fetchQuotations() {
      this.loading = true;
      try {
        await this.$store.dispatch("quotations/getQuotations");
      } finally {
        this.loading = false;
      }
    },

    async fetchCustomers() {
      this.loading = true;
      try {
        await this.$store.dispatch("customers/getCustomers");
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts() {
      this.loading = true;
      try {
        await this.$store.dispatch("products/getProducts");
      } finally {
        this.loading = false;
      }
    },

    async fetchQuotationNumber() {
      try {
        const num = await this.$store.dispatch("quotations/getNextQuotationNumber");
        this.form.quotationNumber = num;
      } catch (e) {
        console.error("Error fetching quotation number", e);
      }
    },

    async autoGenerateQuotation() {
      try {
        await this.getProfile();

        const customer = this.form.customer;

        if (!this.profile || !customer) {
          this.$toast.error("Seller or Customer missing");
          return;
        }

        const seller = {
          ...this.profile,
          address: [this.profile.address, this.profile.city, this.profile.state, this.profile.pincode, this.profile.country]
            .filter(Boolean)
            .join(", "),
        };

        const { jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.width;
        const pageH = doc.internal.pageSize.height;
        const M = 0;
        const CW = pageW;
        const PAD = 5;
        const FOOTER_H = 10;

        const { NAVY, WHITE, BGROW, BRDR } = PDF_COLORS;
        const { sc, rc, fr, wt, rl, fmtDate } = makePdfPrimitives(doc);

        let y = M;

        // ── HEADER — "QUOTATION" badge instead of "TAX INVOICE" ─────────
        const badgeW = 52;
        const badgeH = 12;
        const badgeX = M + CW - badgeW;
        const badgeY = y;
        const leftW = badgeX - M - PAD - 4;
        const addrWrap = wt(seller.address || "", leftW);
        const contactStr = [seller.phone || "", seller.email || ""].filter(Boolean).join("   ·   ");
        const contactWrap = wt(contactStr, leftW);
        const headerH = Math.max(32, 8 + 7 + 3 + addrWrap.length * 3.8 + 2 + contactWrap.length * 3.8 + 4);

        fr(M, y, CW, headerH, NAVY);

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        sc(...WHITE);
        doc.text(seller.businessName || "Company Name", M + PAD + 2, y + 10);

        let hSubY = y + 16;
        hSubY = rl(addrWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);
        hSubY += 2;
        rl(contactWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);

        // Distinct accent (not the invoice's blue) so a quotation is
        // visually distinguishable from a tax invoice at a glance.
        const QUOTE_ACCENT = [180, 130, 20];
        fr(badgeX, badgeY, badgeW, badgeH, QUOTE_ACCENT);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        sc(...WHITE);
        doc.text("QUOTATION", badgeX + badgeW / 2, badgeY + badgeH / 2 + 1.8, { align: "center", charSpace: 0.8 });

        if (seller.gstin) {
          doc.setFontSize(7.5);
          doc.setFont("helvetica", "normal");
          sc(169, 183, 208);
          doc.text(`GSTIN  ${seller.gstin}`, M + CW - 2, badgeY + badgeH + 6, { align: "right" });
        }
        rc();
        y += headerH;

        // ── META ROW — "VALID UNTIL" instead of "DUE DATE" ──────────────
        const infoH = 18;
        fr(M, y, CW, infoH, BGROW);
        doc.setDrawColor(...BRDR);
        doc.setLineWidth(0.3);
        doc.line(M, y + infoH, M + CW, y + infoH);

        const infoFields = [
          { label: "QUOTATION NO", value: this.form.quotationNumber || "-" },
          { label: "QUOTATION DATE", value: fmtDate(this.form.quotationDate) },
          { label: "VALID UNTIL", value: fmtDate(this.form.validUntil) },
          { label: "PLACE OF SUPPLY", value: this.form.placeOfSupply || "-" },
        ];
        const fw = CW / 4;
        infoFields.forEach((f, i) => {
          const fx = M + i * fw + PAD;
          doc.setFontSize(6.5);
          doc.setFont("helvetica", "normal");
          sc(...PDF_COLORS.MUTED);
          doc.text(f.label, fx, y + 7);
          doc.setFontSize(9.5);
          doc.setFont("helvetica", "bold");
          sc(...PDF_COLORS.DARK);
          doc.text(String(f.value), fx, y + 13.5);
        });
        y += infoH + 6;

        // ── BILL TO ──────────────────────────────────────────────────────
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        sc(...PDF_COLORS.MUTED);
        doc.text("QUOTATION FOR", M + PAD, y);
        y += 5;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        sc(...PDF_COLORS.DARK);
        doc.text(customer.name || "-", M + PAD, y);
        y += 5;
        const custAddrWrap = wt(customer.address || "", CW - PAD * 2);
        y = rl(custAddrWrap, M + PAD, y, 8.5, "normal", PDF_COLORS.BODY, 4);
        if (customer.gstin) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          sc(...PDF_COLORS.BODY);
          doc.text(`GSTIN: ${customer.gstin}`, M + PAD, y + 4);
          y += 4;
        }
        y += 8;

        // ── ITEMS TABLE — single flat "Amount" column, no GST breakdown ──
        const rows = this.form.items.map((it) => [
          it.product || "-",
          it.hsn || "-",
          String(it.quantity || 0),
          this.formatAmount(it.price),
          `${it.taxPercent || 0}%`,
          this.formatAmount(it.amount),
        ]);

        autoTable(doc, {
          startY: y,
          head: [["Item", "HSN/SAC", "Qty", "Rate", "Tax %", "Amount"]],
          body: rows,
          margin: { left: M + PAD, right: M + PAD },
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: NAVY, textColor: WHITE },
        });

        y = doc.lastAutoTable.finalY + 8;

        // ── TOTALS — flat estimate, no CGST/SGST/IGST split ─────────────
        const totalsW = 70;
        const totalsX = M + CW - PAD - totalsW;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        sc(...PDF_COLORS.BODY);
        doc.text("Subtotal", totalsX, y);
        doc.text(this.formatAmount(this.subtotal), totalsX + totalsW, y, { align: "right" });
        y += 6;
        doc.text("Estimated Tax", totalsX, y);
        doc.text(this.formatAmount(this.gst), totalsX + totalsW, y, { align: "right" });
        y += 6;
        doc.setDrawColor(...BRDR);
        doc.setLineWidth(0.3);
        doc.line(totalsX, y, totalsX + totalsW, y);
        y += 6;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        sc(...NAVY);
        doc.text("Total", totalsX, y);
        doc.text(this.formatAmount(this.totalAmount), totalsX + totalsW, y, { align: "right" });
        rc();
        y += 12;

        if (this.form.note) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          sc(...PDF_COLORS.MUTED);
          doc.text("NOTE", M + PAD, y);
          y += 5;
          const noteWrap = wt(this.form.note, CW - PAD * 2);
          rl(noteWrap, M + PAD, y, 8.5, "normal", PDF_COLORS.BODY, 4);
        }

        // ── FOOTER (every page) ──────────────────────────────────────────
        const nPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= nPages; p++) {
          doc.setPage(p);
          fr(M, pageH - FOOTER_H, CW, FOOTER_H, NAVY);
          doc.setFontSize(7);
          doc.setFont("helvetica", "normal");
          sc(...WHITE);
          doc.text("This is a computer generated quotation, not a tax invoice.", M + PAD, pageH - FOOTER_H + 6);
          doc.text(`Page ${p} of ${nPages}`, M + CW - PAD, pageH - FOOTER_H + 6, { align: "right" });
        }
        rc();

        const blob = doc.output("blob");
        this.preview = URL.createObjectURL(blob);
        this.file = new File([blob], `quotation-${this.form.quotationNumber}.pdf`, { type: "application/pdf" });
        this.pdfGenerated = true;
        this.$toast.success("Quotation Generated Successfully");
      } catch (e) {
        console.error(e);
        this.$toast.error("Quotation generation failed");
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        let fileBase64 = null;
        if (this.file) fileBase64 = await this.toBase64(this.file);

        const payload = {
          ...this.form,
          subtotal: this.subtotal,
          gst: this.gst,
          totalAmount: this.totalAmount,
          file: fileBase64,
          fileName: this.file?.name,
          fileType: this.file?.type,
        };

        const result = await this.$store.dispatch("quotations/createQuotation", payload);

        this.$toast.success("Created");

        this.reset();

        if (result && result.quotation) {
          this.$router.push(`/quotations/${result.quotation._id}/view`);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error(e?.response?.data?.message || "Error");
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
      this.file = null;
      this.preview = null;
      this.pdfGenerated = false;
      this.quotationMenu = false;
      this.validUntilMenu = false;

      this.form.items = [
        {
          productId: null,
          product: "",
          quantity: 1,
          hsn: "",
          price: 0,
          taxPercent: 18,
          amount: 0,
        },
      ];
    },
  },
};
</script>

<style scoped>
.qt-form-card,
.qt-preview-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.qt-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
}

.qt-form-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.qt-form-header-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qt-form-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

.qt-form-subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.qt-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.qt-item-row {
  position: relative;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 44px 14px 14px;
  margin-bottom: 12px;
}

.qt-item-remove {
  position: absolute;
  top: 6px;
  right: 6px;
}

.qt-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.totals-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  font-size: 13.5px;
  color: #475569;
  justify-items: end;
}
.totals-grid > div:nth-child(odd) {
  justify-self: start;
}

.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  padding-top: 8px;
}

.qt-form-actions {
  padding-top: 12px;
  padding-bottom: 16px;
}

.qt-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.qt-preview-header {
  display: flex;
  align-items: center;
  padding: 18px 20px 8px;
  flex-shrink: 0;
}

.qt-preview-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.qt-generate-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.preview-img {
  max-width: 100%;
}

.preview-frame {
  width: 100%;
  height: 100%;
  flex: 1;
  border: none;
}

.empty-state {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.viewer {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 0;
  padding: 10px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
</style>
